import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBottomBar from "@/components/MobileBottomBar";
import ChatWidget from "@/components/ChatWidget";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import SEOHead from "@/components/SEOHead";
import Home from "@/pages/Home";
import Deliveries from "@/pages/Deliveries";
import MiniMoves from "@/pages/MiniMoves";
import Business from "@/pages/Business";
import Track from "@/pages/Track";
import Contact from "@/pages/Contact";
import Areas from "@/pages/Areas";
import AreaPage from "@/pages/AreaPage";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import CookiesPolicy from "@/pages/CookiesPolicy";
import Launch from "@/pages/Launch";
import Pricing from "@/pages/Pricing";
import Support from "@/pages/Support";
import Referral from "@/pages/Referral";
import Partners from "@/pages/Partners";
import PartnerPage from "@/pages/PartnerPage";
import QuoteStart from "@/pages/quote/QuoteStart";
import QuoteDelivery from "@/pages/quote/QuoteDelivery";
import QuoteMiniMove from "@/pages/quote/QuoteMiniMove";
import QuoteBusiness from "@/pages/quote/QuoteBusiness";
import QuoteConfirm from "@/pages/quote/QuoteConfirm";
import QuoteThankYou from "@/pages/quote/QuoteThankYou";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AuthCallback from "@/pages/admin/AuthCallback";
import { initGA, pageview } from "@/utils/analytics";
import { Toaster } from "@/components/ui/sonner";

// Admin routes without header/footer
function AdminRoutes() {
  const location = useLocation();
  
  // Scroll to top on route change for admin routes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);
  
  // Handle Google OAuth callback - check for session_id in URL fragment
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<AdminLogin />} />
    </Routes>
  );
}

function MainRoutes() {
  const location = useLocation();

  useEffect(() => {
    // Track page views
    pageview(location.pathname + location.search);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/deliveries" element={<Deliveries />} />
          <Route path="/mini-moves" element={<MiniMoves />} />
          <Route path="/business" element={<Business />} />
          <Route path="/track" element={<Track />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/areas" element={<Areas />} />
          <Route path="/areas/:slug" element={<AreaPage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/launch" element={<Launch />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/support" element={<Support />} />
          <Route path="/referral" element={<Referral />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/partners/:slug" element={<PartnerPage />} />
          
          {/* Quote Funnel */}
          <Route path="/quote" element={<QuoteStart />} />
          <Route path="/quote/delivery" element={<QuoteDelivery />} />
          <Route path="/quote/mini-move" element={<QuoteMiniMove />} />
          <Route path="/quote/business" element={<QuoteBusiness />} />
          <Route path="/quote/confirm" element={<QuoteConfirm />} />
          <Route path="/quote/thank-you" element={<QuoteThankYou />} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <MobileBottomBar />
      <ChatWidget />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // Check if this is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Check for OAuth callback with session_id in hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (isAdminRoute) {
    return <AdminRoutes />;
  }
  
  return <MainRoutes />;
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <CookieConsent />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
